<template>
  <!-- LOADING STATE -->
  <div
    v-if="isLoading"
  >
    <div class="flex flex-col gap-4">
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div class="border-b border-gray-100 bg-gray-50 px-6 py-4 flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-800">
            <i class="pi pi-user mr-2 text-primary-500" /> Personal Information
          </h2>
        </div>

        <!-- View Mode -->
        <div>
          <!-- Contact Info -->
          <div class="p-6">
            <Skeleton
              height="25rem"
            />
          </div>

          <!-- Linked Contacts -->
          <div>
            <div class="overflow-hidden">
              <div class="border-b border-gray-100 bg-gray-50 px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-800">
                  <i
                    class="pi pi-users mr-2 text-primary-500"
                  />
                  Linked Contacts
                </h2>
              </div>

              <div class="p-6">
                <Skeleton
                  height="5rem"
                />
              </div>
            </div>
          </div>

          <!-- Groups -->
          <div>
            <div class="overflow-hidden">
              <div class="border-b border-gray-100 bg-gray-50 px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-800">
                  <i class="pi pi-tags mr-2 text-primary-500" /> Groups
                </h2>
              </div>
              <div class="p-6">
                <Skeleton
                  height="5rem"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Active Automations -->
        <div class="border-t border-gray-100">
          <div class="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <h2 class="text-lg font-semibold text-gray-800 flex items-center">
              <i class="pi pi-cog mr-2 text-primary-500" />
              Active Automations
            </h2>
          </div>

          <div class="p-6">
            <Skeleton
              height="5rem"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- LOADED STATE -->
  <div
    v-else
  >
    <div class="flex flex-col gap-4">
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div class="border-b border-gray-100 bg-gray-50 px-6 py-4 flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-800">
            <i class="pi pi-user mr-2 text-primary-500" /> Personal Information
          </h2>

          <div class="flex gap-2">
            <Button
              v-if="!isEditingPersonalInfo"
              label="Edit"
              size="small"
              @click="startEditing"
            >
              <template #icon>
                <Icon
                  name="lucide:edit"
                />
              </template>
            </Button>
          </div>
        </div>

        <!-- View Mode -->
        <div
          v-if="!isEditingPersonalInfo"
          class=""
        >
          <!-- Contact Info -->
          <div class="p-6">
            <div class="grid grid-cols-1 gap-x-8 gap-y-4">
              <div>
                <label class="text-sm font-medium text-gray-500">
                  First Name
                </label>
                <span class="mt-1 text-base font-medium block">
                  {{ contactData.firstName || 'Not specified' }}
                </span>
              </div>
              <div>
                <label class="text-sm font-medium text-gray-500">
                  Last Name
                </label>
                <span class="mt-1 text-base font-medium block">
                  {{ contactData.lastName || 'Not specified' }}
                </span>
              </div>

              <div>
                <label class="text-sm font-medium text-gray-500">
                  Email
                </label>
                <span class="mt-1 text-base block">
                  <a
                    v-if="contactData.email"
                    :href="`mailto:${contactData.email}`"
                    class="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {{ contactData.email }}
                  </a>
                  <span v-else>Not specified</span>

                </span>
              </div>
              <div>
                <label class="text-sm font-medium text-gray-500">
                  Phone
                </label>
                <span class="mt-1 text-base block">
                  <span
                    v-if="contactData.phone"
                  >
                    {{ contactData.phone }}
                  </span>
                  <span v-else>Not specified</span>
                </span>
              </div>

              <div>
                <label class="text-sm font-medium text-gray-500">
                  Address
                </label>
                <span class="mt-1 text-base block">
                  {{ contactData.address?.fullAddress || 'Not specified' }}
                </span>
              </div>
              <div>
                <label class="text-sm font-medium text-gray-500">
                  Created
                </label>
                <span class="mt-1 text-base block">
                  {{ formatDate(contactData.createdAt) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Linked Contacts -->
          <div>
            <div class="overflow-hidden">
              <div class="border-b border-gray-100 bg-gray-50 px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-800">
                  <i
                    class="pi pi-users mr-2 text-primary-500"
                  />
                  Linked Contacts
                </h2>
              </div>

              <div class="p-6">
                <div
                  v-if="contactData.linkedContacts?.length"
                  class="flex flex-col gap-2"
                >
                  <div
                    v-for="record in contactData.linkedContacts"
                    :key="record.id"
                    class="hover:underline"
                  >
                    <NuxtLink
                      target="_blank"
                      :to="{ name: 'contacts-id-activity', params: { id: record.linkedContact.id } }"
                    >
                      <div class="font-medium">
                        {{ record.linkedContact.firstName }}
                        {{ record.linkedContact.lastName }}
                        <span class="font-normal">
                          ({{ getRelationshipName(record.relationship) }})
                        </span>
                      </div>
                    </NuxtLink>
                  </div>
                </div>
                <div
                  v-else
                  class="text-gray-500 italic text-sm"
                >
                  No linked contacts
                </div>
              </div>
            </div>
          </div>

          <!-- Groups -->
          <div>
            <div class="overflow-hidden">
              <div class="border-b border-gray-100 bg-gray-50 px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-800">
                  <i class="pi pi-tags mr-2 text-primary-500" /> Groups
                </h2>
              </div>
              <div class="p-6">
                <div
                  v-if="contactData.contactGroupIds?.length"
                  class="flex flex-wrap gap-2"
                >
                  <Chip
                    v-for="groupId in contactData.contactGroupIds"
                    :key="groupId"
                    :label="getGroupName(groupId)"
                  />
                </div>
                <div
                  v-else
                  class="text-gray-500 italic text-sm"
                >
                  No groups assigned
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit Mode -->
        <div
          v-else
          class=""
        >
          <!-- Contact Info -->
          <div class="">
            <div class=" overflow-hidden">
              <div class="p-6">
                <div class="grid grid-cols-1  gap-4">
                  <div class="flex flex-col gap-2">
                    <label
                      for="firstName"
                      class="text-sm font-medium text-gray-700"
                    >First Name <span class="text-red-500">*</span></label>
                    <InputText
                      id="firstName"
                      v-model="editData.firstName"
                      class="w-full"
                      placeholder="Enter first name"
                      :invalid="errors.firstName"
                    />
                    <small
                      v-if="errors.firstName"
                      class="text-red-500"
                    >
                      First name is required
                    </small>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label
                      for="lastName"
                      class="text-sm font-medium text-gray-700"
                    >Last Name <span class="text-red-500">*</span></label>
                    <InputText
                      id="lastName"
                      v-model="editData.lastName"
                      class="w-full"
                      placeholder="Enter last name"
                      :invalid="errors.lastName"
                    />
                    <small
                      v-if="errors.lastName"
                      class="text-red-500"
                    >
                      Last name is required
                    </small>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label
                      for="email"
                      class="text-sm font-medium text-gray-700"
                    >Email <span class="text-red-500">*</span></label>
                    <InputText
                      id="email"
                      v-model="editData.email"
                      class="w-full"
                      :invalid="errors.email"
                    />
                    <small
                      v-if="errors.email"
                      class="text-red-500"
                    >
                      Valid email is required
                    </small>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label
                      for="phone"
                      class="text-sm font-medium text-gray-700"
                    >
                      Phone
                    </label>

                    <BasePhoneInputWithValidation
                      ref="phoneInputRef"
                      v-model:inputValue="editData.phone"
                      v-model:isErrored="errors.phone"
                    />

                    <small
                      v-if="errors.phone"
                      class="text-red-500"
                    >
                      Valid phone is required
                    </small>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label
                      for="address"
                      class="text-sm font-medium text-gray-700"
                    >Address</label>
                    <BaseGoogleAddress
                      v-model:address="editData.address"
                      :disabledInputField="false"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <!-- Linked Contacts -->
            <div class="bg-white border border-gray-100 overflow-hidden">
              <div class="border-b border-gray-100 bg-gray-50 px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-800">
                  <i class="pi pi-users mr-2 text-primary-500" />
                  Linked Contacts
                </h2>
              </div>
              <div class="p-6">
                <div class="flex flex-col gap-4">
                  <ContactRelationshipAdd @refresh="saveContact" />

                  <div
                    v-if="editData.linkedContacts?.length"
                    class="flex flex-col"
                  >
                    <div
                      v-for="record in editData.linkedContacts"
                      :key="record.id"
                      class="flex items-center justify-between"
                    >
                      <div class="font-medium">
                        {{ record.linkedContact.firstName }}
                        {{ record.linkedContact.lastName }}
                        <span class="font-normal">
                          ({{ getRelationshipName(record.relationship) }})
                        </span>
                      </div>
                      <ContactRelationshipRemove
                        :relationshipId="record.id"
                        @refresh="saveContact"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Groups -->
            <div class="bg-white border border-gray-100 overflow-hidden">
              <div class="border-b border-gray-100 bg-gray-50 px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-800">
                  <i class="pi pi-tags mr-2 text-primary-500" /> Groups
                </h2>
              </div>
              <div class="p-6">
                <div class="flex flex-col gap-2">
                  <label
                    for="groups"
                    class="text-sm font-medium text-gray-700"
                  >Contact Groups</label>
                  <MultiSelect
                    id="groups"
                    v-model="editData.contactGroupIds"
                    :options="contactGroups"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select Groups"
                    class="w-full"
                    filter
                    display="chip"
                  />
                </div>

                <div
                  v-if="editData.contactGroupIds?.length"
                  class="flex flex-wrap gap-2 mt-4"
                >
                  <Chip
                    v-for="groupId in editData.contactGroupIds"
                    :key="groupId"
                    :label="getGroupName(groupId)"
                    class="text-sm "
                  />
                </div>
                <div
                  v-else
                  class="mt-4 text-sm text-gray-500 italic"
                >
                  No groups selected
                </div>
              </div>
            </div>

            <!-- Buttons -->
            <div class="">
              <div class="p-6 flex justify-end gap-2">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  size="small"
                  outlined
                  @click="cancelEditing"
                />
                <Button
                  label="Save Changes"
                  icon="pi pi-check"
                  size="small"
                  :loading="isSavingPersonalInfo"
                  @click="saveContact"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Active Automations -->
        <div class="border-t border-gray-100">
          <div class="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <h2 class="text-lg font-semibold text-gray-800 flex items-center">
              <i class="pi pi-cog mr-2 text-primary-500" />
              Active Automations
            </h2>
          </div>

          <div class="p-6">
            <div
              v-if="contactData?.automations?.length"
              class="space-y-4"
            >
              <div
                v-for="event in contactData?.automations"
                :key="event.id"
                class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
              >
                <div class="p-4 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-gray-100">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <i class="pi pi-play text-primary-600" />
                      </div>
                      <div>
                        <h3 class="font-semibold text-gray-900">
                          {{ event.name }}
                        </h3>
                        <p class="text-sm text-gray-600">
                          {{ event.automationEvents.length }} active events
                        </p>
                      </div>
                    </div>
                    <Badge
                      :value="event.automationEvents.length"
                      class="bg-primary-100 text-primary-800"
                    />
                  </div>
                </div>

                <div class="p-4 space-y-6">
                  <div
                    v-for="automationEvent in event.automationEvents"
                    :key="automationEvent.id"
                    class="bg-gray-50 rounded-lg p-4 border border-gray-100 cursor-pointer"
                    @click="showingAutomationDetail = true, targetAutomation = automationEvent"
                  >
                    <div class="space-y-4">
                      <!-- Progress Section -->
                      <div class="space-y-2">
                        <div class="flex items-center justify-between">
                          <span class="text-sm font-medium text-gray-700">Progress</span>
                          <span class="text-sm text-gray-600">
                            {{ Math.round(getEventProgress(automationEvent?.AutomationEventStep)) }}%
                          </span>
                        </div>
                        <ProgressBar
                          :value="getEventProgress(automationEvent?.AutomationEventStep)"
                          class="h-5"
                        />
                      </div>

                      <!-- Status Section -->
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-3 h-3 rounded-full"
                            :class="{
                              'bg-green-500': automationEvent.currentStatus === 'COMPLETED',
                              'bg-blue-500': automationEvent.currentStatus === 'PENDING',
                              'bg-red-500': automationEvent.currentStatus === 'FAILED',
                              'bg-yellow-500': automationEvent.currentStatus === 'STOPPED',
                            }"
                          />
                          <span class="text-sm font-medium text-gray-700">Status:</span>
                          <span
                            class="px-2 py-1 rounded-full text-xs font-medium"
                            :class="{
                              'bg-green-100 text-green-800': automationEvent.currentStatus === 'COMPLETED',
                              'bg-blue-100 text-blue-800': automationEvent.currentStatus === 'PENDING',
                              'bg-red-100 text-red-800': automationEvent.currentStatus === 'FAILED',
                              'bg-yellow-100 text-yellow-800': automationEvent.currentStatus === 'STOPPED',
                            }"
                          >
                            {{ automationEvent.currentStatus }}
                          </span>
                        </div>

                        <Button
                          v-if="automationEvent.currentStatus === 'PENDING'"
                          size="small"
                          severity="danger"
                          outlined
                          icon="pi pi-stop"
                          label="Stop"
                          class="text-xs"
                          @click="updateAutomationEventStatus(automationEvent?.AutomationEventStep)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="text-center py-12"
            >
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="pi pi-cog text-gray-400 text-2xl" />
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">
                No Active Automations
              </h3>
              <p class="text-gray-600">
                This contact is not part of any automation workflows.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="showingAutomationDetail"
      header="Automation Details"
      :style="{ width: '600px' }"
      :modal="true"
      class="automation-detail-dialog"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <i class="pi pi-cog text-primary-600" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              Automation Progress
            </h3>
            <p class="text-sm text-gray-600">
              Step-by-step execution status
            </p>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <!-- Overall Progress -->
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">Overall Progress</span>
            <span class="text-sm text-gray-600">
              {{ Math.round(getEventProgress(targetAutomation?.AutomationEventStep || [])) }}%
            </span>
          </div>
          <ProgressBar
            :value="getEventProgress(targetAutomation?.AutomationEventStep || [])"
            class="h-5"
          />
        </div>

        <!-- Steps Timeline -->
        <div class="space-y-3">
          <h4 class="text-sm font-medium text-gray-700 mb-3">
            Execution Steps
          </h4>

          <div
            v-for="(eventStep, index) in targetAutomation?.AutomationEventStep || []"
            :key="eventStep.id"
            class="relative"
          >
            <!-- Timeline connector -->
            <div
              v-if="index < (targetAutomation?.AutomationEventStep?.length || 0) - 1"
              class="absolute left-4 top-8 w-0.5 h-12 bg-gray-200"
            />

            <div class="flex items-start gap-3">
              <!-- Status icon -->
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white relative z-10"
                :class="{
                  'border-green-500 bg-green-50': eventStep.status === 'COMPLETED',
                  'border-blue-500 bg-blue-50': eventStep.status === 'PENDING',
                  'border-red-500 bg-red-50': eventStep.status === 'FAILED',
                  'border-yellow-500 bg-yellow-50': eventStep.status === 'STOPPED',

                }"
              >
                <i
                  :class="{
                    'pi pi-check text-green-600': eventStep.status === 'COMPLETED',
                    'pi pi-clock text-blue-600': eventStep.status === 'PENDING',
                    'pi pi-times text-red-600': eventStep.status === 'FAILED',
                    'pi pi-pause text-yellow-600': eventStep.status === 'STOPPED',

                  }"
                  class="text-xs"
                />
              </div>

              <!-- Step content -->
              <div class="flex-1 min-w-0">
                <div class="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div class="flex items-center justify-between mb-2">
                    <h5 class="text-sm font-medium text-gray-900">
                      Step {{ eventStep.step + 1 }}: {{ eventStep.stepType || 'Unknown' }}
                    </h5>
                    <span
                      class="px-2 py-1 rounded-full text-xs font-medium"
                      :class="{
                        'bg-green-100 text-green-800': eventStep.status === 'COMPLETED',
                        'bg-blue-100 text-blue-800': eventStep.status === 'PENDING',
                        'bg-red-100 text-red-800': eventStep.status === 'FAILED',
                        'bg-yellow-100 text-yellow-800': eventStep.status === 'STOPPED',

                      }"
                    >
                      {{ eventStep.status || 'NOT_STARTED' }}
                    </span>
                  </div>

                  <div
                    v-if="eventStep.description"
                    class="text-sm text-gray-600 mb-2"
                  >
                    {{ eventStep.description }}
                  </div>

                  <div class="flex items-center justify-between text-xs text-gray-500">
                    <span v-if="eventStep.createdAt">
                      Created: {{ formatDate(eventStep.createdAt) }}
                    </span>
                    <span v-if="eventStep.updatedAt">
                      Updated: {{ formatDate(eventStep.updatedAt) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            label="Close"
            icon="pi pi-times"
            class="p-button-text"
            @click="showingAutomationDetail = false"
          />
          <Button
            v-if="targetAutomation?.currentStatus === 'PENDING'"
            label="Stop Automation"
            icon="pi pi-stop"
            severity="danger"
            @click="updateAutomationEventStatus(targetAutomation?.AutomationEventStep || []); showingAutomationDetail = false"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="deleteDialogVisible"
      header="Confirm Delete"
      :style="{ width: '450px' }"
      :modal="true"
      :closable="false"
      class="delete-dialog"
    >
      <div class="flex items-center p-4 bg-red-50 rounded-lg mb-4">
        <i
          class="pi pi-exclamation-triangle text-red-500 mr-4"
          style="font-size: 2rem"
        />
        <div>
          <h3 class="font-bold text-red-800 mb-1">
            Delete Contact
          </h3>
          <p class="text-red-700">
            Are you sure you want to delete this contact? This action cannot be undone.
          </p>
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="deleteDialogVisible = false"
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          :loading="isDeleting"
          @click="deleteContact"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import _ from 'lodash'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import isEmail from 'validator/lib/isEmail'
import type { ContactRelationship } from '@prisma/client'

const showingAutomationDetail = ref(false)
const targetAutomation = ref({})

const getEventProgress = (steps) => {
  const total = steps.length
  const completed = steps.filter(s => s.status === 'COMPLETED').length
  const progress = (completed / total) * 100
  return progress
}
const route = useRoute()
const router = useRouter()
const toast = useToast()
const contactId = route.params.contactId as string

const { fetchContactById, fetchContactGroups, updateContact, deleteContact: removeContact } = useContacts()
const isLoading = ref(true)
const isSavingPersonalInfo = ref(false)
const isDeleting = ref(false)
const isEditingPersonalInfo = ref(false)
const contactGroups = ref<Array<{ id: string, name: string }>>([])
const deleteDialogVisible = ref(false)

const contactData = ref({
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  createdAt: '',
  updatedAt: '',
  contactGroupIds: [] as string[],
  linkedContacts: [],
  address: '',
})

const editData = ref({
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  contactGroupIds: [] as string[],
  linkedContacts: [],
  address: '',
})

const errors = ref({
  firstName: false,
  lastName: false,
  email: false,
  phone: false,
})

// Input Phone Number Component
const phoneInputRef = ref()

// Validate Email
const validateEmail = (email: string) => {
  const isValid = isEmail(email)

  return isValid
}

// Validate Phone Number
const formattedContactPhone = ref<string | undefined>()

const validatePhone = (phone: string) => {
  phoneInputRef.value.formatAndValidate(phone.toString())

  if (phoneInputRef.value.isValid) {
    formattedContactPhone.value = phoneInputRef.value.formattedInputNumber.number
  }

  return phoneInputRef.value.isValid
}

const validateForm = (): boolean => {
  errors.value.firstName = !editData.value.firstName.trim()
  errors.value.lastName = !editData.value.lastName.trim()
  errors.value.email
    = !editData.value.email.trim()
      || !validateEmail(editData.value.email)
  errors.value.phone = !editData.value.phone.trim() || !validatePhone(editData.value.phone)

  return !Object.values(errors.value).some(Boolean)
}

const { updateAutomationEventStep } = useAutomationEvents()
const updateAutomationEventStatus = async (events: Array<{ id: string, status: string }>) => {
  try {
    for (const _event of events) {
      console.log('Current Event:', _event)
      if (_event.status === 'PENDING') {
        const updatedStep = await updateAutomationEventStep(_event.id, { status: 'STOPPED' })
        console.log('Updated Step:', updatedStep)
      }
    }

    await fetchContactDetails()
  }
  catch (error) {
    console.error('Error updating automation event step status:', error)
    throw error
  }
}

// Removed unused MainTabs enum

// Removed unused tab variables

const getRelationshipName = (relationship: ContactRelationship) => {
  switch (relationship) {
    case 'BUSINESS_PARTNER':
      return 'Business Partner'
    case 'DUPLICATE_PROFILE':
      return 'Duplicate Profile'
    case 'FAMILY':
      return 'Family'
    case 'SPOUSE':
      return 'Spouse'
  }
}

const getGroupName = (groupId: string) => {
  const group = contactGroups.value.find(g => g.id === groupId)
  return group ? group.name : ''
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'Not available'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  }
  catch {
    return 'Invalid date'
  }
}

// Removed unused linkToBusiness function

const startEditing = () => {
  let parsedContactPhone
  // Check if current contact phone has a valid format including country code
  try {
    const parsedPhone = parsePhoneNumberWithError(contactData.value.phone)

    parsedContactPhone = parsedPhone.number
    // populate input with formatted phone based on country code
    phoneInputRef.value.populateInputWithData(parsedPhone.number)
  }
  catch (error) {
    console.error(error)
    parsedContactPhone = contactData.value.phone
  }

  editData.value = {
    ...contactData.value,
    phone: parsedContactPhone,
    contactGroupIds: [...contactData.value.contactGroupIds],
  }
  isEditingPersonalInfo.value = true
}

const cancelEditing = () => {
  errors.value = {
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  }
  isEditingPersonalInfo.value = false
}

// Removed unused handleTabChange function

const fetchContactDetails = async () => {
  isLoading.value = true

  try {
    const { data, error } = await fetchContactById(contactId)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch contact details',
        life: 3000,
      })
      router.push('/contacts')
      return
    }

    if (!data) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Contact not found',
        life: 3000,
      })
      router.push('/contacts')
      return
    }

    const automations = []
    if (data && data.AutomationEvent) {
      for (const automationEvent of data.AutomationEvent) {
        automations.push(automationEvent.Automation)
      }
    }
    const unique = _.uniqBy(automations, 'id')

    // // user
    const finalAutomations = []
    unique.forEach((automation) => {
      const eventData = data.AutomationEvent.filter(event => event.Automation?.id === automation.id)

      eventData.map((event) => {
        event.AutomationEventStep = event.AutomationEventStep.sort((a, b) => {
          return a.step - b.step
        })

        const failedOnes = event.AutomationEventStep.filter(el => el.status === 'FAILED')
        const completedOnes = event.AutomationEventStep.filter(el => el.status === 'COMPLETED')
        const stoppedOnes = event.AutomationEventStep.filter(el => el.status === 'STOPPED')
        if (failedOnes.length > 0) {
          event.currentStatus = 'FAILED'
        }
        else if (stoppedOnes.length > 0) {
          event.currentStatus = 'STOPPED'
        }
        else if (completedOnes.length === event.AutomationEventStep.length) {
          event.currentStatus = 'COMPLETED'
        }
        else {
          event.currentStatus = 'PENDING'
        }

        delete event.Automation
        return {
          ...event,
        }
      })

      finalAutomations.push({
        name: automation.name,
        id: automation.id,
        automationEvents: eventData,
      })
    })

    console.log('finalAutomations:', finalAutomations)

    contactData.value = {
      id: data.id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      createdAt: data.createdAt || '',
      updatedAt: data.updatedAt || '',
      automations: finalAutomations || [],
      address: data.address || '',
      contactGroupIds: (data as unknown as { contactGroup?: Array<{ id: string }> }).contactGroup?.map(group => group.id) || [],
      linkedContacts: data.linkedContacts || [],
    }
  }
  catch (error) {
    console.error('Error fetching contact details:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch contact details',
      life: 3000,
    })
    router.push('/contacts')
  }
  finally {
    isLoading.value = false
  }
}

const fetchContactGroupsList = async () => {
  try {
    const { data, error } = await fetchContactGroups()

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch contact groups',
        life: 3000,
      })
      return
    }

    contactGroups.value = data || []
  }
  catch (error) {
    console.error('Error fetching contact groups:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch contact groups',
      life: 3000,
    })
  }
}

const saveContact = async () => {
  const isValid = validateForm()

  if (!isValid) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Your form contains invalid data',
      life: 3000,
    })
    throw Error('Failed to create contact - Invalid form data')
  }

  isSavingPersonalInfo.value = true

  try {
    const { error } = await updateContact(editData.value.id, {
      firstName: editData.value.firstName,
      lastName: editData.value.lastName,
      email: editData.value.email,
      phone: formattedContactPhone.value || editData.value.phone,
      contactGroupIds: editData.value.contactGroupIds,
      address: editData.value.address,
    })

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update contact',
        life: 3000,
      })
      return
    }

    contactData.value = {
      ...contactData.value,
      firstName: editData.value.firstName,
      lastName: editData.value.lastName,
      email: editData.value.email,
      phone: editData.value.phone,
      contactGroupIds: [...editData.value.contactGroupIds],
      address: editData.value.address,
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Contact updated successfully',
      life: 3000,
    })

    await fetchContactDetails()
    isEditingPersonalInfo.value = false
  }
  catch (error) {
    console.error('Error updating contact:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update contact',
      life: 3000,
    })
  }
  finally {
    isSavingPersonalInfo.value = false
  }
}

const confirmDeleteContact = () => {
  deleteDialogVisible.value = true
}

const deleteContact = async () => {
  isDeleting.value = true
  try {
    const { error } = await removeContact(contactId)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete contact',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Contact deleted successfully',
      life: 3000,
    })

    deleteDialogVisible.value = false
    router.push('/contacts')
  }
  catch (error) {
    console.error('Error deleting contact:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete contact',
      life: 3000,
    })
  }
  finally {
    isDeleting.value = false
  }
}

onMounted(async () => {
  await Promise.all([
    fetchContactDetails(),
    fetchContactGroupsList(),
  ])
})
</script>
